import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  LinearProgress,
  CircularProgress,
  Chip,
  Box,
  Alert,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  HourglassEmpty as ProcessingIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';
import TranscriptEditorChunked from '../editor/TranscriptEditorChunked';

const TranscriptionCard = ({ transcription, transcriptionNumber, onDelete, onViewDetails, onTranscriptionUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [editorExpanded, setEditorExpanded] = useState(false);
  const [taskStatus, setTaskStatus] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    // Poll for task status if transcription is in progress
    if (transcription.status === 'queued' || transcription.status === 'processing') {
      console.log('[TranscriptionCard] Starting polling for task_id:', transcription.task_id);
      setPolling(true);

      const pollInterval = setInterval(async () => {
        try {
          if (transcription.task_id) {
            const status = await apiService.getTaskStatus(transcription.task_id);
            console.log('[TranscriptionCard] Poll result:', status);
            setTaskStatus(status);

            // Stop polling if completed or failed
            if (status.state === 'SUCCESS' || status.state === 'FAILURE') {
              console.log('[TranscriptionCard] Task finished, calling onTranscriptionUpdate');
              setPolling(false);
              clearInterval(pollInterval);

              // Notify parent to refresh transcription data
              if (onTranscriptionUpdate) {
                onTranscriptionUpdate();
              }
            }
          } else {
            console.log('[TranscriptionCard] No task_id available yet');
          }
        } catch (error) {
          console.error('[TranscriptionCard] Error polling task status:', error);
        }
      }, 2000); // Poll every 2 seconds

      return () => {
        console.log('[TranscriptionCard] Cleaning up polling interval');
        clearInterval(pollInterval);
      };
    } else {
      console.log('[TranscriptionCard] Not starting polling, status is:', transcription.status);
    }
  }, [transcription.status, transcription.task_id, transcription.id, onTranscriptionUpdate]);

  const getStatusIcon = () => {
    if (transcription.status === 'completed') {
      return <CheckIcon color="success" />;
    } else if (transcription.status === 'failed') {
      return <ErrorIcon color="error" />;
    } else {
      return <ProcessingIcon color="primary" />;
    }
  };

  const getStatusColor = () => {
    if (transcription.status === 'completed') return 'success';
    if (transcription.status === 'failed') return 'error';
    if (transcription.status === 'processing') return 'primary';
    return 'default';
  };

  const getProgress = () => {
    if (!taskStatus || !taskStatus.progress) return 0;
    return taskStatus.progress;
  };

  const isTranscribing = () => {
    // Check if we're in the long transcription phase (stuck between 20-40%)
    const progress = getProgress();
    const status = taskStatus?.status || '';
    return (
      (progress >= 20 && progress <= 40) ||
      status.toLowerCase().includes('transcribing') ||
      status.toLowerCase().includes('openai')
    );
  };

  const getStatusMessage = () => {
    const status = taskStatus?.status || transcription.status;
    const progress = getProgress();

    if (isTranscribing()) {
      const model = transcription.llm_model || 'AI model';
      return `Transcribing audio with ${model}... This may take several minutes depending on file size.`;
    }

    return `${status}... ${progress}%`;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {getStatusIcon()}
          <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
            Transcription #{transcriptionNumber || transcription.id}
          </Typography>
          <Chip
            label={transcription.status}
            size="small"
            color={getStatusColor()}
          />
        </Box>

        <Typography variant="body2" color="text.secondary">
          Provider: {transcription.llm_provider}
          {transcription.llm_model && ` (${transcription.llm_model})`}
        </Typography>

        {transcription.processing_time && (
          <Typography variant="caption" color="text.secondary" display="block">
            Processing time: {transcription.processing_time.toFixed(1)}s
          </Typography>
        )}

        {(transcription.status === 'queued' || transcription.status === 'processing') && (
          <Box sx={{ mt: 2 }}>
            {isTranscribing() ? (
              // Show indeterminate spinner during long transcription phase
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  {getStatusMessage()}
                </Typography>
              </Box>
            ) : (
              // Show determinate progress bar for other phases
              <>
                <LinearProgress
                  variant="determinate"
                  value={getProgress()}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {getStatusMessage()}
                </Typography>
              </>
            )}
          </Box>
        )}

        {transcription.status === 'failed' && transcription.error_message && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {transcription.error_message}
          </Alert>
        )}

        {transcription.status === 'completed' && transcription.full_text && (
          <>
            <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                onClick={() => setExpanded(!expanded)}
                endIcon={
                  <ExpandMoreIcon
                    sx={{
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s'
                    }}
                  />
                }
              >
                {expanded ? 'Hide' : 'Show'} Transcript
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditorExpanded(!editorExpanded)}
              >
                {editorExpanded ? 'Hide' : 'Open'} Editor
              </Button>
            </Box>
            <Collapse in={expanded}>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  {transcription.full_text}
                </Typography>
              </Box>
            </Collapse>
            <Collapse in={editorExpanded}>
              <Box sx={{ mt: 2 }}>
                <TranscriptEditorChunked
                  transcriptId={transcription.id}
                  audioFileId={transcription.audio_file_id}
                />
              </Box>
            </Collapse>
          </>
        )}
      </CardContent>

      <CardActions>
        {transcription.status === 'completed' && onViewDetails && (
          <Button size="small" onClick={() => onViewDetails(transcription)}>
            View Details
          </Button>
        )}
        <IconButton
          size="small"
          onClick={() => onDelete(transcription.id)}
          color="error"
          sx={{ ml: 'auto' }}
        >
          <DeleteIcon />
        </IconButton>
      </CardActions>
    </Card>
  );
};

export default TranscriptionCard;
